import type { Option } from "@inrev/core";
import type { KeyboardEvent } from "react";
import type { DocumentViewerController } from "./useDocumentViewer";

type ShortcutKeys = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "target">;

type ShortcutCommands = Pick<
	DocumentViewerController,
	"stepZoom" | "setZoom" | "goToPage" | "page" | "pageCount"
> & { openFind: () => void };

const isTextEntry = (target: EventTarget) =>
	target instanceof HTMLElement &&
	(target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/**
 * The command a key press runs, when it is one of the viewer's shortcuts. Only ⌘/Ctrl F works
 * from a text field, so typing in find or the page input never pages or zooms.
 */
export const shortcutCommand = (
	event: ShortcutKeys,
	{ openFind, stepZoom, setZoom, goToPage, page, pageCount }: ShortcutCommands,
): Option<() => void> => {
	const command = event.metaKey || event.ctrlKey;
	const key = event.key.toLowerCase();
	if (command && key === "f") return openFind;
	if (isTextEntry(event.target)) return undefined;
	if (command && (key === "=" || key === "+")) return () => stepZoom(1);
	if (command && key === "-") return () => stepZoom(-1);
	if (command && key === "0") return () => setZoom("auto");
	if (command || event.altKey) return undefined;
	if (key === "pagedown") return () => goToPage(page + 1);
	if (key === "pageup") return () => goToPage(page - 1);
	if (key === "home") return () => goToPage(1);
	if (key === "end") return () => goToPage(pageCount);
	return undefined;
};
